import axios, { RawAxiosRequestHeaders } from 'axios';
import Cookies from 'universal-cookie';
import General from '../../../general';
import config from '../../../config';
import { Locale } from './QuizTypes';

interface UserMetricsParams {
    entityId: string;
    campaignId: string;
    uniqueUserId?: string;
    uniqueEmailId?: string;
    externalUserId: string;
    questUserId: string;
    questUserToken: string;
    headers: any;
}

export interface ApiHeaders {
    apiKey: string;
    userId?: string;
    token?: string;
    apisecret?: string;
}

interface SubmitFormDataParams {
    isV1Api: any;
    entityId: string;
    campaignId: string;
    answer: Record<string, string | Array<string>>;
    selectedOptions: Record<string, Array<string>>;
    campaignVariationId: string;
    headers: any;
    formdata: any[];
    isAccumulateAndSelf?: boolean;
}

interface GetQuestDataV2 {
    headers: any;
    campaignId: string;
    entityId: string;
    variation?: string;
}

interface GetQuestDataV1 {
    headers: any;
    campaignId: string;
    entityId: string;
    userId: string;
    variation?: string;
}

interface updateTranslationsProps {
    criterias: any[];
    isOption: boolean;
    localeCode: string;
    transition?: any[];
}

class QuizApi {
    private BACKEND_URL: string;
    private GeneralFunctions: General;
    private cookies: Cookies;

    constructor(apiType: string) {
        this.BACKEND_URL =
            apiType === "STAGING" ? config.BACKEND_URL_STAGING : config.BACKEND_URL;
        this.GeneralFunctions = new General("mixpanel", apiType);
        this.cookies = new Cookies();
    }

    private getAxiosHeaders(headers: ApiHeaders): RawAxiosRequestHeaders {
        return {
            "Content-Type": "application/json",
            ...headers,
        };
    }

    async handleUserMetrics({
        entityId,
        campaignId,
        uniqueUserId,
        uniqueEmailId,
        externalUserId,
        questUserId,
        questUserToken,
        headers,
    }: UserMetricsParams) {
        try {
            if (
                externalUserId &&
                questUserId &&
                questUserToken &&
                externalUserId === uniqueUserId
            ) {
                let newHeaders = this.getAxiosHeaders(headers);
                const metricHeaders = {
                    ...newHeaders,
                    userId: questUserId,
                    token: questUserToken,
                };

                await axios.post(
                    `${this.BACKEND_URL}api/entities/${entityId}/users/${questUserId}/metrics/onboarding-view?userId=${questUserId}&campaignId=${campaignId}`,
                    { count: 1 },
                    {
                        params: { userId: questUserId, campaignId },
                        headers: metricHeaders,
                    }
                );
                return { userId: questUserId, token: questUserToken };
            } else if (uniqueUserId) {
                const newHeaders = this.getAxiosHeaders(headers);
                const response = await axios.post(
                    `${this.BACKEND_URL}api/users/external/login`,
                    {
                        externalUserId: uniqueUserId,
                        entityId: entityId,
                        email: uniqueEmailId,
                        ignoreMAU: true,
                    },
                    { headers: this.getAxiosHeaders(headers) }
                );

                const { userId, token } = response.data;

                const date = new Date();
                date.setHours(date.getHours() + 12);

                this.cookies.set("externalUserId", uniqueUserId, {
                    path: "/",
                    expires: date,
                });
                this.cookies.set("questUserId", userId, { path: "/", expires: date });
                this.cookies.set("questUserToken", token, { path: "/", expires: date });

                const metricHeaders = { ...newHeaders, userId, token };
                await axios.post(
                    `${this.BACKEND_URL}api/entities/${entityId}/users/${questUserId}/metrics/onboarding-view?userId=${questUserId}&campaignId=${campaignId}`,
                    { count: 1 },
                    {
                        params: { userId, campaignId },
                        headers: metricHeaders,
                    }
                );
                return { userId, token };
            }
        } catch (error) {
            this.GeneralFunctions.captureSentryException(error);
        }
    }

    async submitFormData({
        isV1Api,
        entityId,
        campaignId,
        answer,
        selectedOptions,
        campaignVariationId,
        headers,
        formdata,
        isAccumulateAndSelf,
    }: SubmitFormDataParams) {
        try {
            let newHeaders = this.getAxiosHeaders(headers);
            const combinedAnswer = { ...answer };
            Object.keys(selectedOptions).forEach((key) => {
                combinedAnswer[key] = selectedOptions[key];
            });

            if (!isV1Api) {
                const actions = Object.keys(combinedAnswer).map(key => ({
                    actionId: key,
                    answers: Array.isArray(combinedAnswer[key]) ? combinedAnswer[key] : [combinedAnswer[key]],
                    question: formdata[formdata.findIndex(ele => ele.actionId === key)]?.question,
                }));

                const response = await axios.post(
                    `${this.BACKEND_URL}api/v2/entities/${entityId}/campaigns/${campaignId}/verify`,
                    { actions, campaignVariationId },
                    { headers: newHeaders }
                );
                return response.data;
            } else {
                const criterias = Object.keys(combinedAnswer).map(key => ({
                    criteriaId: key,
                    answer: Array.isArray(combinedAnswer[key]) ? combinedAnswer[key] : [combinedAnswer[key]],
                    question: formdata[formdata.findIndex(ele => ele.criteriaId === key)]?.question,
                }));

                const response = await axios.post(
                    `${this.BACKEND_URL}api/entities/${entityId}/quests/${campaignId}/verify-all?userId=${headers.userId}`,
                    { criterias, userId: headers.userId, isAccumulateAndSelf },
                    { headers: newHeaders }
                );
                return response.data;
            }
        } catch (error) {
            this.GeneralFunctions.captureSentryException(error);
            throw error;
        }
    }

    async fetchLocalesAndTranslations() {
        const projectId = config.PHRASE_PROJECT_ID;
        const accessToken = config.PHRASE_ACCESS_TOKEN;
        try {
            const localeResponse = await fetch(
                `https://api.phrase.com/v2/projects/${projectId}/locales`,
                {
                    headers: {
                        Authorization: `token ${accessToken}`,
                        "Content-Type": "application/json"
                    }
                }
            );

            const localesData: Locale[] = await localeResponse.json();
            const filteredLocales = localesData.filter(
                locale => locale.id !== "1c7c617229fb6d738a62f213edee028d"
            );

            return filteredLocales;
        } catch (error) {
            this.GeneralFunctions.captureSentryException(error);
            throw error;
        }
    }

    async getTranslation(entityId, locale) {
        const projectId = config.PHRASE_PROJECT_ID;
        const accessToken = config.PHRASE_ACCESS_TOKEN;
        try {
            const queryParams = new URLSearchParams({
                q: `tags:${entityId},static`,
            }).toString();

            const translationResponse = await fetch(
                `https://api.phrase.com/v2/projects/${projectId}/locales/${locale.id}/download?file_format=json&${queryParams}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `token ${accessToken}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            const translations = await translationResponse.json();
            return Object.entries(translations).map(([key, value]) => ({
                key,
                message: (value as { message: string }).message,
                localeId: locale.id,
                name: locale.name,
            }));
        } catch (error) {
            this.GeneralFunctions.captureSentryException(error);
            throw error;
        }
    }

    async getQuestDataV2({
        headers,
        campaignId,
        entityId,
        variation,
    }: GetQuestDataV2) {
        try {
            let params = new URLSearchParams();
            params.set("platform", "REACT");
            if (variation) params.set("variation", variation);

            const url = `${this.BACKEND_URL}api/v2/entities/${entityId}/campaigns/${campaignId}?${params.toString()}`;
            const response = await axios.get(url, { headers: this.getAxiosHeaders(headers) });
            return response.data;
        } catch (error) {
            this.GeneralFunctions.captureSentryException(error);
            throw error;
        }
    }

    async getQuestDataV1({
        headers,
        campaignId,
        entityId,
        userId,
        variation,
    }: GetQuestDataV1) {
        try {
            const url = `${this.BACKEND_URL}api/entities/${entityId}/quests/${campaignId}/criterias?userId=${userId}&getVariation=${variation || false}`;
            const { data } = await axios.get(url, { headers: this.getAxiosHeaders(headers) });
            return data;
        } catch (error) {
            this.GeneralFunctions.captureSentryException(error);
            throw error;
        }
    }

    async updateTranslations({
        criterias,
        isOption,
        localeCode,
        transition
    }: updateTranslationsProps) {
        try {
            const updatedCriterias = criterias.map((criteria) => {
                const translation = transition?.find(
                    (t) =>
                        t.name === localeCode &&
                        t.key.includes(criteria.criteriaId) &&
                        !t.key.includes("OPTION")
                );
                // if (translation) {
                //     return {
                //         ...criteria,
                //         question: translation.message,
                //         options: isOption
                //             ? criteria.options.map((option: string) => {
                //                 const translation = transition?.find(
                //                     (t: any) =>
                //                         t.name === localeCode &&
                //                         t.key == `${criteria.criteriaId} - ${option} OPTION`
                //                 );
                //                 if (translation) {
                //                     return translation.message;
                //                 }
                //                 return option;
                //             })
                //             : criteria?.options,
                //     };
                // }
                return criteria;
            });

            return updatedCriterias;
        } catch (error) {
            this.GeneralFunctions.captureSentryException(error);
            return criterias;
        }
    }
}

export default QuizApi;